import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import { getEssay } from '../../data/essays';
import { ArrowLeft } from 'lucide-react';

const essay = getEssay('certainty-is-now-free')!;

const CertaintyIsNowFree = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": essay.title,
    "description": essay.subtitle,
    "datePublished": essay.date,
    "dateModified": essay.dateModified ?? essay.date,
    "author": {
      "@type": "Person",
      "name": essay.author,
      "url": "https://www.linkedin.com/in/kent-frazier/",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ParadoxEdge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.paradoxedge.com/essays/${essay.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${essay.title} | Essays`}
        description={essay.subtitle}
        canonicalUrl={`/essays/${essay.slug}`}
        ogType="article"
        ogImage={essay.ogImage}
        ogImageAlt="Certainty is now free: an essay by Kent H. Frazier"
        publishedTime={essay.date}
        modifiedTime={essay.dateModified ?? essay.date}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1">
          <article className="py-20 sm:py-28">
            <div className="container-mobile max-w-[42rem]">
              {/* Back link */}
              <Link
                to="/essays"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" />
                All essays
              </Link>

              {/* Header */}
              <header className="mb-12 sm:mb-16">
                <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-primary font-semibold mb-6">
                  Essay
                </p>
                <h1 className="mb-6">{essay.title}</h1>
                <p className="font-display text-xl sm:text-2xl text-foreground/95 leading-snug mb-8 italic">
                  No amount of knowing will tell you
                  <br className="md:hidden" />{' '}
                  what to do next.
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground border-t border-border pt-6">
                  <span className="font-semibold text-foreground">By {essay.author}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={essay.date}>{essay.dateDisplay}</time>
                  <span aria-hidden="true">·</span>
                  <span>{essay.readingMinutes} min read</span>
                </div>
              </header>

              {/* Body */}
              <div className="essay-prose">
                <p>Forward Deployed Engineering has been one of the fastest-growing roles in tech.</p>
                <p>Talking with a long-time client and Global Forward Deployment Engineering leader this week, I learned that the language “forward deployed” is becoming hollowed out by overuse, and people are now using the word “frontier” to describe engineering in the context of AI Business Transformation.</p>

                <p>The edge, I said.</p>

                <p>The edge, he said.</p>

                <p>Yes.</p>

                <p>Forward deployed presumes a center, or “knowing place” exists somewhere.</p>

                <p>You are sent from the mothership. You are expected to know. The consultant-client relationship is built on this agreement. The client doesn't know, so they hire a consultant who does.</p>

                <p>The edge breaks that agreement.</p>

                <p>What happens when no one knows?</p>


                <p>Let’s look at the frontier.</p>

                <p>A frontier implies settlement.</p>

                <p>We land. Plant something. It becomes the center for whoever comes next.</p>

                <p>The edge moves every time we learn something. It calls us to attend to our relationship with the unknown, over and over, rather than a settled location.</p>

                <p>Nobody gets to finish arriving.</p>

                <p>We live at the threshold of the unknown unknowns.</p>

                <p>The machine now delivers certainty on demand.</p>

                <p>Fluent. Confident. Instant. Free.</p>

                <p>It commoditizes what is already known.</p>

                {/* Section break */}
                <div className="not-prose my-16 sm:my-20 flex items-center justify-center gap-6">
                  <span className="h-px w-20 bg-primary/30" />
                  <span className="inline-block w-2 h-2 bg-primary/50 rotate-45" />
                  <span className="h-px w-20 bg-primary/30" />
                </div>

                <p>Working at the edge means knowing is not available.</p>

                <p>What’s painful is that our knowing is our identity, status, and the currency we have been paid for since our first jobs.</p>

<p>Not knowing arrives as a threat in the body well before it reaches the mind. Having an understanding of this does not change our involuntary reactions under the intense discomfort of not knowing.</p>

                <p>Leading at this edge means we will be wrong in front of people.</p>

                <p>Failure is normal here.</p>

                <p>Being wrong feels exiling.</p>

                <p>Our ability to keep creating at the edge depends on what we are able to survive being wrong about.</p>

                <p>The models know and everyone buys the same ones.</p>

                <p>The precious resource is the human who can live into the questions this moment is asking of us, exercise choice when an immediate and plausible answer is a machine prompt away, and stand accountable for what happens next.</p>

                

                <p>Who do you become when it’s not possible to know the answer until you are living it?</p>
              </div>

              {/* Author block */}
              <aside className="mt-20 pt-10 border-t border-border">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  About the author
                </p>
                <p className="text-foreground leading-relaxed">
                  Kent Frazier is the founder of ParadoxEdge. A former HR executive and Certified Integral Master Coach, he has spent three decades inside the work of leadership, partnering with senior leaders to develop the inner capacities and outer capabilities that meet the moment.
                </p>
                <div className="mt-8">
                  <a
                    href="https://calendly.com/kentfrazier/30-minutes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300"
                  >
                    Begin a Conversation
                  </a>
                </div>
              </aside>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CertaintyIsNowFree;
